import { useMemo, useState } from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  USER_LOAN_ESTIMATION_TYPE,
  USER_LOAN_PROGRAM,
  USER_QUALIFY_STATUS,
} from '@/config/constants';
import {
  useCreateLoanApplicationMutation,
  useGetMyLoanApplicationsQuery,
  useGetOrCreatePurchaseLoanApplicationMutation,
} from '@/apis/extended/loanApplicationApi';
import { useGetCurrentUserQuery } from '@/apis/extended/userApi';
import { LoanHubApplicationCard } from '@/components/CommercialLoanApplication/LoanHubApplicationCard';
import { LoanHubFlowGuide } from '@/components/CommercialLoanApplication/LoanHubFlowGuide';
import { TastePageHeader } from '@/components/Common/TastePageHeader';
import { TasteSectionCard } from '@/components/Common/TasteSectionCard';
import { PaywallModal } from '@/components/Subscription/PaywallModal';
import { useInvestorAccess } from '@/hooks/useInvestorAccess';
import {
  dedupePurchaseAppsForHub,
  groupApplicationsForHub,
  pickRefinanceApplication,
  refinanceProductTypeFromProgram,
  type LoanHubFlowBucket,
} from '@/utils/loanApplicationHub';
import {
  hubIntentMatchesApplication,
  resolveHubCreateIntent,
} from '@/utils/profileLoanApplicationIntent';

const SECTION_HEADINGS: Record<
  LoanHubFlowBucket,
  { title: string; empty: string }
> = {
  purchase: {
    title: 'Purchase files',
    empty:
      'No purchase application yet. Start one below or from a saved property.',
  },
  investor: {
    title: 'Investor & DSCR files',
    empty: 'No investor or DSCR purchase file yet.',
  },
  refinance: {
    title: 'Refinance files',
    empty:
      'No refinance application here yet. Start from your refinance summary or a refinance property.',
  },
};

export function CommercialLoanHubPage() {
  const navigate = useNavigate();
  const { checkAccess } = useInvestorAccess();
  const [paywallOpen, setPaywallOpen] = useState(false);
  const { data: user } = useGetCurrentUserQuery();
  const { data: apps = [], isLoading } = useGetMyLoanApplicationsQuery();
  const displayApps = useMemo(() => dedupePurchaseAppsForHub(apps), [apps]);
  const grouped = useMemo(
    () => groupApplicationsForHub(displayApps),
    [displayApps]
  );
  const [createApp, { isLoading: creating }] =
    useCreateLoanApplicationMutation();
  const [getOrCreateApp, { isLoading: creatingRefinance }] =
    useGetOrCreatePurchaseLoanApplicationMutation();

  const isQualified = user?.qualifyStatus === USER_QUALIFY_STATUS.QUALIFY;
  const isRefinanceProfile =
    user?.loanEstimationType === USER_LOAN_ESTIMATION_TYPE.REFINANCE;

  const hubIntent = useMemo(
    () =>
      resolveHubCreateIntent(
        user?.loanEstimationType as USER_LOAN_ESTIMATION_TYPE | undefined,
        user?.loanProgram as USER_LOAN_PROGRAM | undefined
      ),
    [user?.loanEstimationType, user?.loanProgram]
  );

  const matchingApp = useMemo(() => {
    if (!hubIntent.canCreate) return null;
    return (
      displayApps.find((a) =>
        hubIntentMatchesApplication(hubIntent, a.flowProfile, a.productType)
      ) ?? null
    );
  }, [displayApps, hubIntent]);

  const existingRefinanceApp = useMemo(
    () => pickRefinanceApplication(apps),
    [apps]
  );

  const startOrContinueRefinance = async () => {
    if (existingRefinanceApp) {
      navigate(`/loan/${existingRefinanceApp._id}`);
      return;
    }
    try {
      const app = await getOrCreateApp({
        flowProfile: 'refinance',
        productType: refinanceProductTypeFromProgram(
          user?.loanProgram as USER_LOAN_PROGRAM | undefined
        ),
        approvalChannel: 'loan_hub',
      }).unwrap();
      navigate(`/loan/${app._id}`);
    } catch {
      toast.error('Could not start refinance application. Try again.');
    }
  };

  const startOrContinue = async () => {
    if (!hubIntent.canCreate) return;
    if (matchingApp) {
      navigate(`/loan/${matchingApp._id}`);
      return;
    }
    if (!checkAccess(hubIntent.paywallLoanProgram)) {
      setPaywallOpen(true);
      return;
    }
    try {
      const app = await createApp({
        productType: hubIntent.productType,
        flowProfile: hubIntent.flowProfile,
        investorModeAtCreation: hubIntent.investorModeAtCreation,
      }).unwrap();
      navigate(`/loan/${app._id}`);
    } catch {
      toast.error('Could not create application. Try again.');
    }
  };

  const paywallProgram =
    hubIntent.canCreate && hubIntent.paywallLoanProgram
      ? hubIntent.paywallLoanProgram
      : USER_LOAN_PROGRAM.DSCR;

  if (!isQualified) {
    return (
      <Container sx={{ py: 4, maxWidth: 960 }} className="taste-rise">
        <TastePageHeader title="Mortgage applications" />
        <TasteSectionCard sx={{ p: 3 }}>
          <Typography variant="body1" color="text.secondary" paragraph>
            Complete your loan qualifications before you can start or manage
            mortgage applications here.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/loan/qualifications')}
          >
            Go to loan qualifications
          </Button>
        </TasteSectionCard>
      </Container>
    );
  }

  const totalFiles = displayApps.length;

  return (
    <Container sx={{ py: 4, maxWidth: 960 }} className="taste-rise">
      <PaywallModal
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        selectedLoanProgram={paywallProgram}
        onUpgraded={() => setPaywallOpen(false)}
      />

      <TastePageHeader
        title="Mortgage applications"
        subtitle="Purchase and refinance use different workflows. Purchase property details stay with each listing; refinance property details stay in the application file."
      />

      {!hubIntent.canCreate && !isRefinanceProfile ? (
        <TasteSectionCard sx={{ p: 2.5, mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {hubIntent.userMessage}
          </Typography>
        </TasteSectionCard>
      ) : null}

      <LoanHubFlowGuide
        purchaseCanCreate={hubIntent.canCreate}
        onStartPurchase={
          hubIntent.canCreate ? () => void startOrContinue() : undefined
        }
        purchaseLoading={creating}
        hasPurchaseApplication={Boolean(matchingApp)}
        purchaseIntentPhrase={
          hubIntent.canCreate ? hubIntent.intentPhrase : undefined
        }
        onStartRefinance={startOrContinueRefinance}
        refinanceLoading={creatingRefinance}
        hasRefinanceApplication={Boolean(existingRefinanceApp)}
      />

      {isLoading ? (
        <Typography color="text.secondary">Loading applications…</Typography>
      ) : totalFiles === 0 ? (
        <TasteSectionCard sx={{ p: 3 }}>
          <Typography color="text.secondary">
            No applications yet. Use the purchase or refinance card above to
            start a file.
          </Typography>
        </TasteSectionCard>
      ) : (
        <Stack spacing={3}>
          {(['purchase', 'investor', 'refinance'] as LoanHubFlowBucket[]).map(
            (bucket) => {
              const items = grouped[bucket];
              if (items.length === 0) return null;
              const heading = SECTION_HEADINGS[bucket];
              return (
                <Box key={bucket}>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>
                    {heading.title}
                  </Typography>
                  <Stack spacing={1.5}>
                    {items.map((a) => (
                      <LoanHubApplicationCard
                        key={a._id}
                        application={a}
                        onOpen={() => navigate(`/loan/${a._id}`)}
                      />
                    ))}
                  </Stack>
                </Box>
              );
            }
          )}
        </Stack>
      )}
    </Container>
  );
}
